/**
 * @author       calvin@zumper.com (Calvin Yau)
 * @copyright    Copyright (c) 2021, Zumper
 * @description  get page type from location
 */

import { selectIsSingleUnit } from '@zcode-ui/perm-address'
import { isDetailPageNew, selectIsAddressOnBuilding } from '@zcode-ui/web-zumper-global-state'
import { matchPath, matchRoutes } from '@zcode/router-utils'
import type { State } from '@zcode/state-type'
import {
  buildingDetailRoutes,
  currentSearchRoutes,
  homepageRoutes,
  listingDetailRoutes,
  permanentAddressRoutes,
  rentResearchRoutes,
  routes,
  routesConstants,
  sitemapRoutes,
} from '@zcode/zumper-routes'
import type { Location } from 'history'
import {
  addressOnBuildingPageType,
  buildingPageType,
  defaultPageType,
  homepagePageType,
  listingOldPageType,
  listingPageType,
  mapListAFRPageType,
  mapListCFRPageType,
  mapListHFRPageType,
  mapListRFRPageType,
  multiUnitAddressPageType,
  rentResearchDetailNewPageType,
  rentResearchHomepageNewPageType,
  signInPageType,
  signUpPageType,
  singleUnitAddressPageType,
  siteMapChildPageType,
  siteMapEntryPointPageType,
} from '../server/utils/determinePageType'
import { PROPERTY_TYPE_PATH_RE } from './urls/constants'

// TODO: There is similar logic at nzr/src/server/utils/determinePageType.ts that we use on the server and at
// nzr/src/modules/mixpanel/helpers/getPageNameFromRouteProps.js that we use for mixpanel. Each serves a slightly
// different purpose currently but we should eventually consolidate. This version is used in the client and uses the same page type variables as
// determinePageType which matches up with our current grafana dashboards for server metrics. The mixpanel version uses
// a set of constants that don't match up 1-to-1.
const _getPageType = (state: State, location?: Location<unknown>) => {
  if (typeof window === 'undefined' && typeof location === 'undefined') {
    return null
  }
  const pathname = location?.pathname || window.location.pathname
  let pageType = defaultPageType
  if (listingDetailRoutes.some((route) => matchPath(pathname, route))) {
    if (isDetailPageNew(state)) {
      pageType = listingPageType
    } else {
      pageType = listingOldPageType
    }
  } else if (buildingDetailRoutes.some((route) => matchPath(pathname, route))) {
    pageType = selectIsAddressOnBuilding(state) ? addressOnBuildingPageType : buildingPageType
  } else if (currentSearchRoutes.some((route) => matchPath(pathname, route))) {
    const propertyTypePath = (PROPERTY_TYPE_PATH_RE.exec(pathname) ?? [])[0]
    if (propertyTypePath === routesConstants.CONDOS_BASE_URL) {
      pageType = mapListCFRPageType
    } else if (propertyTypePath === routesConstants.HOUSES_BASE_URL) {
      pageType = mapListHFRPageType
    } else if (propertyTypePath === routesConstants.ROOMS_BASE_URL) {
      pageType = mapListRFRPageType
    } else {
      pageType = mapListAFRPageType
    }
  } else if (homepageRoutes.some((route) => matchPath(pathname, route))) {
    pageType = homepagePageType
  } else if (permanentAddressRoutes.some((route) => matchPath(pathname, route))) {
    pageType = selectIsSingleUnit(state) ? singleUnitAddressPageType : multiUnitAddressPageType
  } else if (matchPath(pathname, rentResearchRoutes[0])) {
    pageType = rentResearchHomepageNewPageType
  } else if (rentResearchRoutes.slice(1).some((route) => matchPath(pathname, route))) {
    pageType = rentResearchDetailNewPageType
  } else if (matchPath(pathname, sitemapRoutes[0])) {
    pageType = siteMapEntryPointPageType
  } else if (matchPath(pathname, sitemapRoutes[1])) {
    const matches =
      /sitemap\/(addresses|rent_research|blog|rent_calculator|search|listings|buildings)/.exec(
        pathname
      )
    const childPageType = pathname.split('/')[2]
    if (matches?.[1]) {
      pageType = `${siteMapChildPageType}-${childPageType}`
    }
  } else {
    const matchedRouteNames = matchRoutes(
      routes,
      location || {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        state: undefined,
      },
      { useSwitch: false }
    ).map(({ route: { name } }) => name)
    if (matchedRouteNames.includes(routesConstants.SIGN_IN)) {
      return signInPageType
    }
    if (matchedRouteNames.includes(routesConstants.SIGN_UP)) {
      return signUpPageType
    }
  }
  return pageType
}

export const getPageType = (state: State, location: Location<unknown>) =>
  _getPageType(state, location)

/**
 * This function returns null on the server, and only returns the actual page type on the client.
 *
 * If you use this in SSR, you'll introduce a hydration error. It's only safe to use in client-only
 * code, like in an `onClick` handler. You can use `getPageType(state, location)` safely in SSR and CSR code.
 */
export const getPageTypeIPromiseImOnlyCallingThisClientSide = (state: State) => _getPageType(state)
